import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Typography,
  FormControl,
  FormLabel,
  Stack,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Info as InfoIcon,
  Add as AddIcon
} from '@mui/icons-material';

// Define available fields for different entity types
const ENTITY_FIELDS = {
  task: {
    basic: [
      { value: 'title', label: 'Title', type: 'string', description: 'Task title' },
      { value: 'description', label: 'Description', type: 'string', description: 'Task description' },
      { value: 'status', label: 'Status', type: 'enum', description: 'Task status (to_do, in_progress, etc.)' },
      { value: 'priority', label: 'Priority', type: 'enum', description: 'Task priority (low, medium, high, urgent)' },
      { value: 'dueDate', label: 'Due Date', type: 'date', description: 'Task due date' },
      { value: 'startedAt', label: 'Start Date', type: 'date', description: 'When task was started' },
      { value: 'completedAt', label: 'Completion Date', type: 'date', description: 'When task was completed' },
      { value: 'estimatedHours', label: 'Estimated Hours', type: 'number', description: 'Estimated hours for task' },
      { value: 'actualHours', label: 'Actual Hours', type: 'number', description: 'Actual hours spent' },
      { value: 'type', label: 'Task Type', type: 'enum', description: 'Type of task' },
      { value: 'isClientVisible', label: 'Client Visible', type: 'boolean', description: 'Whether task is visible to client' }
    ],
    relationships: [
      { value: 'assignedTo', label: 'Assigned To', type: 'user', description: 'User assigned to the task' },
      { value: 'createdBy', label: 'Created By', type: 'user', description: 'User who created the task' },
      { value: 'projectId', label: 'Project', type: 'project', description: 'Project the task belongs to' },
      { value: 'milestoneId', label: 'Milestone', type: 'milestone', description: 'Associated milestone' },
      { value: 'sectionId', label: 'Section', type: 'section', description: 'Task section' },
      { value: 'parentTaskId', label: 'Parent Task', type: 'task', description: 'Parent task if subtask' }
    ],
    arrays: [
      { value: 'tags', label: 'Tags', type: 'array', description: 'Task tags' },
      { value: 'collaborators', label: 'Collaborators', type: 'array', description: 'Task collaborators' },
      { value: 'attachments', label: 'Attachments', type: 'array', description: 'Task attachments' },
      { value: 'comments', label: 'Comments', type: 'array', description: 'Task comments' },
      { value: 'checklist', label: 'Checklist', type: 'array', description: 'Task checklist items' }
    ],
    nested: [
      { value: 'collaborators.user', label: 'Collaborator User', type: 'user', description: 'Specific collaborator' },
      { value: 'collaborators.status', label: 'Collaborator Status', type: 'enum', description: 'Collaborator status' },
      { value: 'checklist.isCompleted', label: 'Checklist Completed', type: 'boolean', description: 'Checklist item completion' }
    ]
  },
  content: {
    basic: [
      { value: 'title', label: 'Title', type: 'string', description: 'Content title' },
      { value: 'text', label: 'Text', type: 'string', description: 'Content text' },
      { value: 'status', label: 'Status', type: 'enum', description: 'Content status' },
      { value: 'contentType', label: 'Content Type', type: 'enum', description: 'Type of content' },
      { value: 'publishDate', label: 'Publish Date', type: 'date', description: 'Scheduled publish date' },
      { value: 'isClientVisible', label: 'Client Visible', type: 'boolean', description: 'Visibility to client' }
    ],
    relationships: [
      { value: 'author', label: 'Author', type: 'user', description: 'Content author' },
      { value: 'projectId', label: 'Project', type: 'project', description: 'Associated project' },
      { value: 'taskId', label: 'Task', type: 'task', description: 'Associated task' }
    ]
  },
  user: {
    basic: [
      { value: 'firstName', label: 'First Name', type: 'string', description: 'User first name' },
      { value: 'lastName', label: 'Last Name', type: 'string', description: 'User last name' },
      { value: 'email', label: 'Email', type: 'string', description: 'User email address' },
      { value: 'role', label: 'Role', type: 'enum', description: 'User role' },
      { value: 'isActive', label: 'Active', type: 'boolean', description: 'Whether user is active' }
    ]
  },
  project: {
    basic: [
      { value: 'name', label: 'Name', type: 'string', description: 'Project name' },
      { value: 'description', label: 'Description', type: 'string', description: 'Project description' },
      { value: 'status', label: 'Status', type: 'enum', description: 'Project status' },
      { value: 'startDate', label: 'Start Date', type: 'date', description: 'Project start date' },
      { value: 'endDate', label: 'End Date', type: 'date', description: 'Project end date' },
      { value: 'budget', label: 'Budget', type: 'number', description: 'Project budget' }
    ],
    relationships: [
      { value: 'client', label: 'Client', type: 'client', description: 'Project client' },
      { value: 'manager', label: 'Manager', type: 'user', description: 'Project manager' }
    ]
  }
};

const FieldSelector = ({ 
  entityType = 'task',
  selectedFields = [],
  onChange,
  label = 'Select Fields',
  helperText,
  multiple = true,
  disabled = false,
  variant = 'chips', // 'chips' or 'list'
  showCategories = true
}) => {
  const [inputValue, setInputValue] = useState('');

  // Get available fields based on entity type
  const availableFields = useMemo(() => {
    const fields = ENTITY_FIELDS[entityType] || ENTITY_FIELDS.task;
    const allFields = [];
    
    if (showCategories) {
      // Group by category
      Object.entries(fields).forEach(([category, categoryFields]) => {
        categoryFields.forEach(field => {
          allFields.push({
            ...field,
            category,
            displayLabel: `${field.label} (${category})`,
            id: field.value
          });
        });
      });
    } else {
      // Flatten all fields
      Object.values(fields).forEach(categoryFields => {
        categoryFields.forEach(field => {
          allFields.push({
            ...field,
            displayLabel: field.label,
            id: field.value
          });
        });
      });
    }
    
    return allFields;
  }, [entityType, showCategories]);

  // Filter out already selected fields from options
  const availableOptions = useMemo(() => {
    return availableFields.filter(field => !selectedFields.includes(field.value));
  }, [availableFields, selectedFields]);

  const handleFieldAdd = (event, newValue) => {
    if (multiple) {
      const fieldValues = newValue.map(item => 
        typeof item === 'string' ? item : item.value
      );
      onChange(fieldValues);
    } else {
      const fieldValue = typeof newValue === 'string' ? newValue : newValue?.value;
      onChange(fieldValue ? [fieldValue] : []);
    }
  };

  const handleFieldRemove = (fieldValue) => {
    onChange(selectedFields.filter(f => f !== fieldValue));
  };

  const getFieldInfo = (fieldValue) => {
    return availableFields.find(f => f.value === fieldValue);
  };

  const renderChipsView = () => (
    <Box>
      <Autocomplete
        multiple={multiple}
        options={availableOptions}
        getOptionLabel={(option) => option.displayLabel || option}
        value={selectedFields.map(f => getFieldInfo(f) || { value: f, label: f, displayLabel: f })}
        onChange={handleFieldAdd}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            helperText={helperText || `${selectedFields.length} field(s) selected`}
            placeholder="Type to search fields..."
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2">{option.label}</Typography>
              {option.description && (
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              )}
              {showCategories && (
                <Chip 
                  label={option.category} 
                  size="small" 
                  sx={{ ml: 1 }} 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}
        renderTags={(value, getTagProps) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 0.5 }}>
            {value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.label || option}
                size="small"
                {...getTagProps({ index })}
                onDelete={() => handleFieldRemove(option.value || option)}
              />
            ))}
          </Box>
        )}
      />
      
      {selectedFields.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Selected Fields:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {selectedFields.map(fieldValue => {
              const fieldInfo = getFieldInfo(fieldValue);
              return (
                <Tooltip 
                  key={fieldValue}
                  title={fieldInfo?.description || fieldValue}
                >
                  <Chip
                    label={fieldInfo?.label || fieldValue}
                    onDelete={() => handleFieldRemove(fieldValue)}
                    color="primary"
                    size="small"
                    icon={
                      fieldInfo?.type === 'date' ? <InfoIcon /> :
                      fieldInfo?.type === 'user' ? <InfoIcon /> :
                      null
                    }
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderListView = () => (
    <Box>
      <FormControl fullWidth>
        <FormLabel>{label}</FormLabel>
        <Paper variant="outlined" sx={{ mt: 1, maxHeight: 300, overflow: 'auto' }}>
          <List dense>
            {selectedFields.map(fieldValue => {
              const fieldInfo = getFieldInfo(fieldValue);
              return (
                <ListItem key={fieldValue}>
                  <ListItemText
                    primary={fieldInfo?.label || fieldValue}
                    secondary={fieldInfo?.description}
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      size="small"
                      onClick={() => handleFieldRemove(fieldValue)}
                      disabled={disabled}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
            {selectedFields.length === 0 && (
              <ListItem>
                <ListItemText 
                  primary="No fields selected"
                  secondary="Use the field selector above to add fields"
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </FormControl>
      
      <Autocomplete
        options={availableOptions}
        getOptionLabel={(option) => option.displayLabel || option}
        value={null}
        onChange={(event, newValue) => {
          if (newValue) {
            const fieldValue = typeof newValue === 'string' ? newValue : newValue.value;
            if (!selectedFields.includes(fieldValue)) {
              onChange([...selectedFields, fieldValue]);
            }
          }
        }}
        disabled={disabled}
        sx={{ mt: 2 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add Field"
            placeholder="Search and select a field..."
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2">{option.label}</Typography>
              {option.description && (
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      />
    </Box>
  );

  return variant === 'chips' ? renderChipsView() : renderListView();
};

export default FieldSelector;